import React, { useCallback, useContext, useMemo, useState } from "react";
import { IconButton, Menu, MenuItem } from "@material-ui/core";
import { useFilterContext } from "./State/FilterContext";
import { useFilterDispatchContext } from "./State/FilterDispatchContext";
import { FeedModalStackContext, FeedModalTypes } from "./SearchFeed";
import { MoreVert } from "@material-ui/icons";
import { useInsightsPanelContext } from "../Insights/InsightsPanelContext";
import {
  doesFilterContainsStaleFilters,
  isDefaultFilter,
  isLegacyFilter,
  UnnamedGroopitFilterDisplayDTO,
} from "./GroopitFilterDTO";
import { useGroupContext } from "groopit-js";
import { useFeedContext } from "./State/FeedContext";
import { createFilterResetFilterAction } from "./State/FilterActions";
import { NamedGroopitFilterDisplayDTO } from "groopit-js/client";
import { SaveFilterDialog } from "./SaveFilterDialog";
import {
  TypingIndicator,
  TypingIndicatorVariant,
} from "../Icons/TypingIndicator";

export const FeedFilterMobile = () => {
  const { user_id, group, owner, users } = useGroupContext();
  const { filter } = useFilterContext();
  const feed = useFeedContext();
  const { filterDispatcher, setFilterMode } = useFilterDispatchContext();
  const { totalCount } = useFeedContext();
  const { pushModalStack } = useContext(FeedModalStackContext);
  const insightsPanelContext = useInsightsPanelContext();
  const canUpdatefilter =
    filter instanceof NamedGroopitFilterDisplayDTO &&
    (owner || filter.creator.id === user_id);

  const [filterDataMenuAnchor, setFilterDataMenuAnchor] =
    useState<HTMLElement>();
  const isFilterDataMenuOpen = Boolean(filterDataMenuAnchor);

  const filterContainsStaleFilters = useMemo(
    () => doesFilterContainsStaleFilters(filter, group, users),
    [filter, group, users],
  );

  const [showSaveFilterDialog, setShowSaveFilterDialog] = useState<
    [showDialog: boolean, mode: "save" | "save_as"]
  >([false, "save"]);

  const createReport = useCallback(() => {
    insightsPanelContext.setIsGeneratingReport({
      filter: filter.filter,
      groopitCount: totalCount,
      reportCompleted: false,
      error: null,
    });
  }, [totalCount, filter, insightsPanelContext]);

  return (
    <div>
      <div className="flex items-center">
        <div className="flex-grow">
          <div className="italic">
            Showing {isDefaultFilter(filter) ? "all" : "filtered"} data (
            {feed.loading ? (
              <TypingIndicator variant={TypingIndicatorVariant.Small} />
            ) : (
              <span>{feed.totalCount} results</span>
            )}
            )
          </div>
          {isLegacyFilter(filter) && (
            <p>
              Currently showing legacy filters. Note that opening the filter
              menu will clear the legacy filters.
            </p>
          )}
        </div>
        <IconButton
          id="filter-data-menu-button"
          className="place-self-start"
          aria-controls="filter-data-menu"
          aria-haspopup="true"
          aria-expanded={isFilterDataMenuOpen ? "true" : undefined}
          aria-label="Filter Data"
          onClick={(event) => setFilterDataMenuAnchor(event.currentTarget)}
        >
          <MoreVert className="text-app-black" />
        </IconButton>
        <Menu
          id="filter-data-menu"
          anchorEl={filterDataMenuAnchor}
          open={isFilterDataMenuOpen}
          onClose={() => setFilterDataMenuAnchor(undefined)}
          MenuListProps={{
            "aria-labelledby": "filter-data-menu-button",
          }}
        >
          <MenuItem
            onClick={() => {
              setFilterMode("edit");
              pushModalStack?.(FeedModalTypes.Filters);
              setFilterDataMenuAnchor(undefined);
            }}
          >
            Edit filters
          </MenuItem>
          <MenuItem
            onClick={() => {
              setFilterMode("saved");
              pushModalStack?.(FeedModalTypes.Filters);
              setFilterDataMenuAnchor(undefined);
            }}
          >
            Apply saved filters
          </MenuItem>
          {!isDefaultFilter(filter) && (
            <MenuItem
              onClick={() => {
                filterDispatcher(createFilterResetFilterAction());
                setFilterDataMenuAnchor(undefined);
              }}
            >
              Clear filters
            </MenuItem>
          )}
          {!isDefaultFilter(filter) &&
            filter instanceof NamedGroopitFilterDisplayDTO &&
            canUpdatefilter && (
              <MenuItem
                onClick={() => {
                  setShowSaveFilterDialog([true, "save"]);
                  setFilterDataMenuAnchor(undefined);
                }}
              >
                Resave filters
              </MenuItem>
            )}
          {!isDefaultFilter &&
            filter instanceof NamedGroopitFilterDisplayDTO &&
            !canUpdatefilter && (
              <MenuItem
                onClick={() => {
                  setShowSaveFilterDialog([true, "save_as"]);
                  setFilterDataMenuAnchor(undefined);
                }}
              >
                Save as new filter
              </MenuItem>
            )}
          {!isDefaultFilter &&
            filter instanceof UnnamedGroopitFilterDisplayDTO && (
              <MenuItem
                onClick={() => {
                  setShowSaveFilterDialog([true, "save"]);
                  setFilterDataMenuAnchor(undefined);
                }}
              >
                Save filters
              </MenuItem>
            )}
          <MenuItem
            onClick={() => {
              createReport();
              pushModalStack?.(FeedModalTypes.Insights);
              setFilterDataMenuAnchor(undefined);
            }}
          >
            Generate Summary Insight
          </MenuItem>
          <MenuItem
            onClick={() => {
              pushModalStack?.(FeedModalTypes.Insights);
              setFilterDataMenuAnchor(undefined);
            }}
          >
            View Summary Insights
          </MenuItem>
        </Menu>
        <SaveFilterDialog
          filter={filter}
          open={showSaveFilterDialog[0]}
          mode={showSaveFilterDialog[1]}
          onClose={() => setShowSaveFilterDialog([false, "save"])}
        />
      </div>
      {filterContainsStaleFilters && (
        <p>
          <i>Note:</i> this saved filter uses fields that are no longer part of
          the data model. Please click the menu button, edit the filter, and
          resave it.
        </p>
      )}
    </div>
  );
};
